/**
 * Trust-first onboarding: purpose chips, settings-aware announcement, and FAQ.
 * Keep all customer-facing strings here or in JOURNEY_GUIDE_COPY — not in templates.
 */

export type TrustFirstPurposeId =
  | 'understand_team_work'
  | 'catch_issues_early'
  | 'prove_compliance'
  | 'reduce_idle_waste';

export interface TrustFirstPurposeOption {
  id: TrustFirstPurposeId;
  label: string;
  /** Clause for “so we can …” in the announcement. */
  announcementClause: string;
}

export const TRUST_FIRST_PURPOSES: TrustFirstPurposeOption[] = [
  {
    id: 'understand_team_work',
    label: 'See what work is happening',
    announcementClause: 'see what work is happening',
  },
  {
    id: 'catch_issues_early',
    label: 'Apps and websites in use',
    announcementClause: 'see which apps and websites are in use',
  },
  {
    id: 'prove_compliance',
    label: 'Attendance and work hours',
    announcementClause: 'keep a clearer record of attendance and work hours',
  },
  {
    id: 'reduce_idle_waste',
    label: 'Where work time goes',
    announcementClause: 'see where work time goes',
  },
];

export interface TrustFirstCollectionSnapshot {
  appsOn: boolean;
  hoursIncluded: boolean;
  screenshotsOn: boolean;
  screenshotIntervalMinutes: number | null;
  liveViewAllowed: boolean;
  liveViewAuditNote: boolean;
  employeeControlled: boolean;
  trayOn: boolean;
  shiftLabel: string | null;
}

export interface TrustFirstAnnounceContext {
  purposeId: string | null;
  purposeOtherText: string | null;
  collection: TrustFirstCollectionSnapshot;
  viewerLabel: string;
}

export function purposeAnnouncementClause(
  purposeId: string | null,
  purposeOtherText: string | null
): string {
  if (purposeId === 'other') {
    const trimmed = (purposeOtherText || '').trim();
    return trimmed || 'have clearer visibility into how work is happening';
  }
  const found = TRUST_FIRST_PURPOSES.find((p) => p.id === purposeId);
  if (found) {
    return found.announcementClause;
  }
  const earlier: Record<string, string> = {
    work_hours_workload: 'see how the workday actually went',
    fair_delivery_followups: 'follow up more fairly when delivery slips',
    remote_hybrid_support: 'support people who work remotely or hybrid',
  };
  return earlier[purposeId || ''] || 'have clearer visibility into how work is happening';
}

export function buildTrustFirstAnnouncement(ctx: TrustFirstAnnounceContext): string {
  const purpose = purposeAnnouncementClause(ctx.purposeId, ctx.purposeOtherText);
  const records: string[] = [];
  if (ctx.collection.appsOn) {
    records.push('apps and websites');
  }
  if (ctx.collection.hoursIncluded) {
    records.push('active, idle, and timesheet hours');
  }
  const recordsPhrase =
    records.length > 0 ? records.join(', and ') : 'work activity on company computers';

  const screenshotSentence = ctx.collection.screenshotsOn
    ? `Screenshots are on, about every ${
        ctx.collection.screenshotIntervalMinutes || 15
      } minutes, so we can review context when a timesheet or update is unclear.`
    : 'Screenshots are off.';

  const liveSentence = ctx.collection.liveViewAllowed
    ? ctx.collection.liveViewAuditNote
      ? 'Live view is allowed for the people named above, and those views can be logged.'
      : 'Live view is allowed for the people named above.'
    : 'Live view is off.';

  const schedule = ctx.collection.shiftLabel
    ? `during ${ctx.collection.shiftLabel}`
    : 'during the company work schedule';
  const modePhrase = ctx.collection.employeeControlled
    ? 'they can start and stop it'
    : 'admins start and stop it';
  const traySentence = ctx.collection.trayOn
    ? 'SuperSee runs as a visible app on the computer.'
    : 'SuperSee runs as a silent app in the background. We are still telling you this is in use.';

  return [
    `We’re introducing SuperSee on company work computers so we can ${purpose}.`,
    `It records ${recordsPhrase}. It does not record keystrokes or microphone audio.`,
    `${screenshotSentence} ${liveSentence}`,
    `Tracking runs ${schedule} and ${modePhrase}. ${traySentence}`,
    `${ctx.viewerLabel} can view team data. We’ll use this for delivery and workload conversations, not to rank people.`,
    'Questions: reply to this email.',
  ].join('\n\n');
}

export interface TrustFirstAnnounceFact {
  label: string;
  value: string;
  caution?: boolean;
}

/** Short scan of the same settings the note describes. */
export function buildTrustFirstAnnounceFacts(ctx: TrustFirstAnnounceContext): TrustFirstAnnounceFact[] {
  const c = ctx.collection;
  const records: string[] = [];
  if (c.appsOn) {
    records.push('Apps and websites');
  }
  if (c.hoursIncluded) {
    records.push('Hours');
  }
  return [
    { label: 'Records', value: records.length ? records.join(' · ') : 'Work activity' },
    {
      label: 'Screenshots',
      value: c.screenshotsOn
        ? `On · every ${c.screenshotIntervalMinutes || 15} min`
        : 'Off',
    },
    { label: 'Live view', value: c.liveViewAllowed ? 'On' : 'Off' },
    {
      label: 'Control',
      value: c.employeeControlled ? 'Employee can start / stop' : 'Admin starts and stops',
    },
    {
      label: 'App',
      value: c.trayOn ? 'Visible' : 'Silent — say so in the note',
      caution: !c.trayOn,
    },
    { label: 'When', value: c.shiftLabel || 'Work schedule' },
    { label: 'Who can view', value: ctx.viewerLabel },
  ];
}

export interface TrustFirstFaqItem {
  question: string;
  answer: string;
}

export function buildTrustFirstFaq(ctx: TrustFirstAnnounceContext): TrustFirstFaqItem[] {
  const c = ctx.collection;
  const recordBits: string[] = [];
  if (c.appsOn) {
    recordBits.push('apps and websites');
  }
  if (c.hoursIncluded) {
    recordBits.push('active and idle time and timesheet hours');
  }
  if (c.screenshotsOn) {
    recordBits.push('screenshots');
  }
  if (c.liveViewAllowed) {
    recordBits.push('live view when a manager opens it');
  }
  const recordsAnswer =
    recordBits.length > 0
      ? `It records ${recordBits.join(', ')}. It does not record keystrokes or microphone audio.`
      : 'It records work activity on company computers. It does not record keystrokes or microphone audio.';

  const chatsAnswer =
    c.screenshotsOn || c.liveViewAllowed
      ? 'SuperSee does not open your chat or email apps to read messages. If screenshots or live view are on, whatever is on the screen can be seen.'
      : 'With screenshots and live view off, SuperSee records apps, sites, and time — not message contents.';

  const seeRunning = c.trayOn
    ? 'Yes. SuperSee runs as a visible app on the computer.'
    : 'SuperSee runs as a silent app in the background. Your admin should still tell you it is in use.';

  const canPause = c.employeeControlled
    ? 'Yes. You can start and stop tracking yourself in this setup.'
    : 'No. Tracking runs automatically in this setup. Ask your admin if you need something changed.';

  const ssLive = [
    c.screenshotsOn
      ? `Screenshots are on (about every ${c.screenshotIntervalMinutes || 15} minutes).`
      : 'Screenshots are off.',
    c.liveViewAllowed ? 'Live view is allowed.' : 'Live view is off.',
  ].join(' ');

  return [
    { question: 'What does it record?', answer: recordsAnswer },
    { question: 'Does it read chats or email?', answer: chatsAnswer },
    { question: 'Will I see it running?', answer: seeRunning },
    { question: 'Can I pause it?', answer: canPause },
    {
      question: 'Who can look at my data?',
      answer: `${ctx.viewerLabel} can review team data for delivery and workload conversations.`,
    },
    { question: 'Are screenshots or live view on?', answer: ssLive },
  ];
}

export function buildTrustFirstAckTemplate(ctx: TrustFirstAnnounceContext): string {
  return (
    `I understand SuperSee is being introduced on company work computers so we can ${purposeAnnouncementClause(
      ctx.purposeId,
      ctx.purposeOtherText
    )}. ` +
    // Guidance like "not a legal document" belongs in the admin-facing label, not in text the employee signs.
    'I have read the team note about what is recorded, who can view it, and how tracking runs.'
  );
}

export function formatFaqForCopy(items: TrustFirstFaqItem[]): string {
  return items.map((item, i) => `${i + 1}. ${item.question}\n${item.answer}`).join('\n\n');
}
